import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateEmail,
  validateIFSC,
  validateAccountNumber
} from '../../../../utils/validationUtils';

/**
 * Validation function for individual fields
 */
const validateField = (fieldName, value) => {
  if (!value || value.trim() === '') {
    return null; // Don't validate empty fields in edit mode
  }

  switch (fieldName) {
    case 'panNumber':
      return validatePAN(value) === true ? null : validatePAN(value);
    case 'gstNumber':
    case 'gstNumberPrimary':
      return validateGST(value) === true ? null : validateGST(value);
    case 'mobileNumber':
    case 'contactPersonMobile':
      return validateMobile(value) === true ? null : validateMobile(value);
    case 'emailAddress':
    case 'contactPersonEmail':
      return validateEmail(value) === true ? null : validateEmail(value);
    case 'ifscCode':
      return validateIFSC(value) === true ? null : validateIFSC(value);
    case 'accountNumber':
      return validateAccountNumber(value) === true ? null : validateAccountNumber(value);
    default:
      return null;
  }
};

export const useEditDetails = (clientData, onSave, onClose) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (clientData?.entity) {
      // console.log('📥 Original API Data:', clientData.entity);

      // Normalize the data to match dropdown values
      const normalizedData = {
        ...clientData.entity,
        // Normalize entity type values
        entityType: normalizeEntityType(clientData.entity.entityType),
        // Normalize LOB type values  
        lobType: normalizeLOBType(clientData.entity.lobType),
        // Normalize LOB nationality values
        lobNationality: normalizeLOBNationality(clientData.entity.lobNationality),
      };

      // console.log('✅ Normalized Data:', {
      //   entityType: normalizedData.entityType,
      //   lobType: normalizedData.lobType,
      //   lobNationality: normalizedData.lobNationality,
      // });

      setFormData(normalizedData);
    }
  }, [clientData]);

  // Helper functions to normalize values to match form options
  const normalizeEntityType = (value) => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();

    // Map API values to form option values
    const mapping = {
      'proprietorship': 'proprietorship',
      'partnership': 'partnership',
      'llp': 'llp',
      'private_limited': 'private_limited',
      'public_limited': 'public_limited',
      'trust': 'trust',
      'society': 'society',
      // If API returns "client" or "vendor", it's not the entity type, return empty
      'client': '',
      'vendor': ''
    };
    return mapping[normalized] || value;
  };

  const normalizeLOBType = (value) => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();
    const mapping = {
      'travel': 'travel',
      'company': 'company',
      'auto': 'auto',
      'gadget': 'gadget',
      'non-travel': 'company',
      'nontravel': 'company',
    };
    return mapping[normalized] || value;
  };

  const normalizeLOBNationality = (value) => {
    if (!value) return "";
    const normalized = value.toLowerCase().trim();
    const mapping = {
      'domestic': 'Domestic',
      'international': 'international',
    };
    return mapping[normalized] || value;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Auto-uppercase for specific fields
    const uppercaseFields = ['panNumber', 'gstNumber', 'ifscCode'];
    if (uppercaseFields.includes(name) && typeof newValue === 'string') {
      newValue = newValue.toUpperCase();
    }

    // Only allow numbers for mobile number and account number fields
    if ((name === 'mobileNumber' || name === 'accountNumber') && typeof newValue === 'string') {
      newValue = newValue.replace(/\D/g, '');
    }

    setFormData({
      ...formData,
      [name]: newValue
    });

    // Live validation - validate as user types
    if (type !== 'checkbox') {
      const error = validateField(name, newValue);

      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Additional validation on blur
    const error = validateField(name, value);

    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error('Failed to update entity');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  const getDocumentStatusBadge = (doc) => {
    if (doc.isUploaded) {
      return <span className="doc-status uploaded">✓ Uploaded</span>;
    }
    return <span className="doc-status pending">⏳ Pending</span>;
  };

  const getFieldValidationState = (fieldName) => {
    const value = formData[fieldName];
    const error = errors[fieldName];
    const hasValue = value && value.toString().trim() !== '';
    const hasValidation = ['panNumber', 'gstNumber', 'mobileNumber', 'emailAddress', 'ifscCode', 'accountNumber'].includes(fieldName);
    const isValid = hasValue && !error && hasValidation;

    return {
      error,
      isValid,
      className: error ? 'error' : isValid ? 'success' : ''
    };
  };

  return {
    formData,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleClose,
    getDocumentStatusBadge,
    getFieldValidationState,
  };
};
